import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Modal,
  Typography,
  Button
} from '@mui/material';
import { Radar, Line } from 'react-chartjs-2';
import {
  Chart,
  RadialLinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { CategoryScale, LinearScale, Title } from 'chart.js';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

// Use environment variable for API URL
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

// register chart.js components
Chart.register(RadialLinearScale, PointElement, LineElement, Tooltip, Legend, CategoryScale, LinearScale, Title);

const Leaderboards = () => {
  const [scales, setScales] = useState([]);
  const [selectedScale, setSelectedScale] = useState('');
  const [loadingScales, setLoadingScales] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [results, setResults] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeAgent, setActiveAgent] = useState(null);
  const chartRef = useRef();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchScales = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${API_URL}/api/simulations/operators`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Fetched scales:', response.data);
        setScales(response.data);
        setLoadingScales(false);
      } catch (error) {
        console.error('Error fetching scales:', error);
        setLoadingScales(false);
      }
    };

    fetchScales();
  }, [getAccessTokenSilently]);

  const handleScaleChange = (e) => {
    // const scaleId = e.target.value;
    // setSelectedScale(scaleId);
    // setLoadingResults(true);
    // fetch(`/api/simulations/operators/${scaleId}/leaderboard`)
    //   .then(res => res.json())
    //   .then(data => {
    //     setResults(data);
    //     setLoadingResults(false);
    //   })
    //   .catch(() => setLoadingResults(false));

    const scaleId = e.target.value;
    setSelectedScale(scaleId);
    setLoadingResults(true);
    const dbScaleId = scales.find(scale => scale.id === scaleId)._id;
    const fetchResults = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${API_URL}/api/simulations/${dbScaleId}/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Fetched results:', response.data);
        setResults(response.data);
        setLoadingResults(false);
      } catch (error) {
        console.error('Error fetching results:', error);
        setLoadingResults(false);
      }
    };
    fetchResults();
  };

  const handleChartClick = (evt) => {
    const chart = chartRef.current;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(evt.nativeEvent, 'nearest', { intersect: true }, true);
    if (elements.length) {
      const { datasetIndex } = elements[0];
      const agent = results && results[datasetIndex];
      if (agent) {
        setActiveAgent(agent);
        setModalOpen(true);
      }
    }
  };

  // Prepare chart data and options when results are available
  let chartData = {};
  let chartOptions = {};
  let useRadar = false;
  if (results) {
    // Get factor labels from selected scale
    const scaleObj = scales.find(s => s.id === selectedScale);
    const labels = scaleObj.factors.map(f => f.label);
    // Compute max score for radial scale
    const allScores = results.flatMap(agent => agent.factors.map(f => f.score));
    const maxScore = Math.ceil(Math.max(...allScores));
    // Define colors
    const colors = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40'];
    // Build datasets with scores and items
    const datasets = results.map((agent, idx) => ({
      label: agent.agentName,
      data: agent.factors.map(f => f.score),
      items: agent.factors.map(f => f.items),
      fill: false,
      borderWidth: 2,
      borderColor: colors[idx % colors.length],
      backgroundColor: colors[idx % colors.length] + '33'
    }));
    // choose chart type based on number of factors
    if (labels.length >= 3) {
      useRadar = true;
      chartData = { labels, datasets };
      chartOptions = {
        responsive: true,
        scales: { r: { min: 0, max: maxScore, ticks: { beginAtZero: true } } },
        plugins: {
          tooltip: {
            callbacks: {
              label: context => {
                const ds = context.dataset;
                const idx = context.dataIndex;
                const score = context.formattedValue;
                const items = ds.items[idx];
                const factor = context.label;
                return `${ds.label} - ${factor}: ${score} (items: ${items})`;
              }
            }
          },
          legend: { position: 'top' }
        },
        onClick: handleChartClick
      };
    } else {
      useRadar = false;
      chartData = { labels, datasets };
      chartOptions = {
        responsive: true,
        scales: {
          x: { type: 'category', title: { display: true, text: 'Factors' } },
          y: { min: 0, max: maxScore, title: { display: true, text: 'Score' } }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: context => {
                const ds = context.dataset;
                const idx = context.dataIndex;
                const score = context.formattedValue;
                const items = ds.items[idx];
                const factor = context.label;
                return `${ds.label} - ${factor}: ${score} (items: ${items})`;
              }
            }
          },
          legend: { position: 'top' }
        },
        onClick: handleChartClick
      };
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Leaderboards
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="scale-select-label">Select Scale</InputLabel>
        {loadingScales ? (
          <CircularProgress size={24} />
        ) : (
          <Select
            labelId="scale-select-label"
            value={selectedScale}
            label="Select Scale"
            onChange={handleScaleChange}
          >
            {scales.map(scale => (
              <MenuItem key={scale.id} value={scale.id}>
                {scale.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>

      {loadingResults && <CircularProgress />}

      {results && (
        useRadar ? (
          <Radar ref={chartRef} data={chartData} options={chartOptions} />
        ) : (
          <Line  ref={chartRef} data={chartData} options={chartOptions} />
        )
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper', p: 4, borderRadius: 1, boxShadow: 24, maxWidth: 400
        }}>
          {activeAgent && (
            <>
              <Typography variant="h6" gutterBottom>
                {activeAgent.name}
              </Typography>
              {activeAgent.values.map(v => (
                <Typography key={v.label}>{v.label}: {v.score}</Typography>
              ))}
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button onClick={() => setModalOpen(false)}>Close</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Leaderboards;
